import { useCallback, useMemo, useState } from 'react';
import { useGetUserPreferences, useUpdateUserPreferences } from '~/api/auth';
import { useDebounce } from '~/hooks/useDebounce';
import {
  preferenceSlot,
  usePreferenceWriteQueue,
} from '~/hooks/usePreferenceWriteQueue';
import { buildBuyListMatchInput } from './buyListFilters';
import {
  readBuyListTeamActive,
  rowsToFilterData,
} from './buyListFilterStorage';
import { useBuyListSavedViews } from './useBuyListSavedViews';

const TEAM_PERSIST_MS = 500;

const EMPTY_TEAM_MEMBER_IDS: string[] = [];

export function useBuyListFilters() {
  const { data: preferencesData, isLoading } = useGetUserPreferences();
  const { mutateAsync: updatePreferencesAsync } = useUpdateUserPreferences();
  const enqueueWrite = usePreferenceWriteQueue(
    preferenceSlot('buyLists', 'BUYING')
  );

  const preferences = preferencesData?.userPreferences;
  const savedTeamMemberIds =
    preferences?.buyListTeamMemberIds ?? EMPTY_TEAM_MEMBER_IDS;

  const [localTeamMemberIds, setLocalTeamMemberIds] = useState<string[] | null>(
    null
  );
  const [isTeamActive, setIsTeamActive] = useState<boolean | null>(null);

  const teamMemberIds = localTeamMemberIds ?? savedTeamMemberIds;
  const hasTeam = teamMemberIds.length > 0;
  const savedTeamActive = readBuyListTeamActive(preferences?.buyListFilter);
  const teamActive = isTeamActive ?? savedTeamActive ?? hasTeam;

  // Owns the filter rows and their anonymous persistence; this hook keeps only the
  // team picker, which is not part of a named preset.
  const views = useBuyListSavedViews(teamActive);
  const rows = views.rows;

  const persistTeamActive = useDebounce((nextTeamActive: boolean) => {
    enqueueWrite(() =>
      updatePreferencesAsync({
        preferences: {
          buyListFilter: rowsToFilterData(rows, nextTeamActive),
        },
      })
    ).catch(() => {});
  }, TEAM_PERSIST_MS);

  const setTeamActive = useCallback(
    (nextTeamActive: boolean) => {
      setIsTeamActive(nextTeamActive);
      persistTeamActive(nextTeamActive);
    },
    [persistTeamActive]
  );

  const saveTeam = useCallback(
    (nextTeamMemberIds: string[]) => {
      const nextHasTeam = nextTeamMemberIds.length > 0;
      const nextTeamActive = !nextHasTeam ? false : hasTeam ? teamActive : true;
      setLocalTeamMemberIds(nextTeamMemberIds);
      setIsTeamActive(nextTeamActive);
      persistTeamActive.cancel();
      enqueueWrite(() =>
        updatePreferencesAsync({
          preferences: {
            buyListTeamMemberIds: nextTeamMemberIds,
            buyListFilter: rowsToFilterData(rows, nextTeamActive),
          },
        })
      ).catch(() => {});
    },
    [
      enqueueWrite,
      updatePreferencesAsync,
      persistTeamActive,
      rows,
      hasTeam,
      teamActive,
    ]
  );

  const match = useMemo(() => buildBuyListMatchInput(rows), [rows]);
  const buyerIds = teamActive && hasTeam ? teamMemberIds : undefined;

  return {
    isLoading: isLoading || (views.isLoadingViews && !views.isViewsError),
    rows,
    setRows: views.setRows,
    views,
    teamMemberIds,
    hasTeam,
    teamActive,
    setTeamActive,
    saveTeam,
    buyerIds,
    match,
  };
}
